// Card component for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.2

import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { tokens } from '../../../styles/tokens'
import {
  CardProps,
  CardHeaderProps,
  CardContentProps,
  CardFooterProps,
} from './card.types'

// Styled card container
const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: tokens.radius.lg,
  overflow: 'hidden',
}))

// Styled card header
const CardHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '18px 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

// Styled card content
const CardContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'noPadding',
})<{ noPadding?: boolean }>(({ noPadding }) => ({
  padding: noPadding ? 0 : '18px 24px',
}))

// Styled card footer
const CardFooterContainer = styled(Box)(({ theme }) => ({
  padding: '14px 24px',
  borderTop: `1px solid ${theme.palette.divider}`,
}))

// Main Card component
export const Card: React.FC<CardProps> = ({ children, sx, className }) => {
  return (
    <CardContainer sx={sx} className={className}>
      {children}
    </CardContainer>
  )
}

// Card Header component
export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  actions,
  sx,
}) => {
  return (
    <CardHeaderContainer sx={sx}>
      <Box>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.4,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: 13,
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Box>{actions}</Box>}
    </CardHeaderContainer>
  )
}

// Card Content component
export const CardContent: React.FC<CardContentProps> = ({
  children,
  noPadding = false,
  sx,
}) => {
  return (
    <CardContentContainer noPadding={noPadding} sx={sx}>
      {children}
    </CardContentContainer>
  )
}

// Card Footer component
export const CardFooter: React.FC<CardFooterProps> = ({ children, sx }) => {
  return <CardFooterContainer sx={sx}>{children}</CardFooterContainer>
}

Card.displayName = 'Card'
CardHeader.displayName = 'CardHeader'
CardContent.displayName = 'CardContent'
CardFooter.displayName = 'CardFooter'

export default Card
export type {
  CardProps,
  CardHeaderProps,
  CardContentProps,
  CardFooterProps,
} from './card.types'
